import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Wrench, LogOut, Menu, X, Camera, Fingerprint, Tag, Layers, Warehouse } from "lucide-react";

const NAV_ITEMS = [
  { to: "/photo",    label: "Photo",    testid: "nav-photo",    icon: Camera },
  { to: "/vin",      label: "VIN",      testid: "nav-vin",      icon: Fingerprint },
  { to: "/tag",      label: "Body Tag", testid: "nav-tag",      icon: Tag },
  { to: "/identify", label: "Combined", testid: "nav-combined", icon: Layers },
];

const desktopLink = ({ isActive }) =>
  `font-display uppercase tracking-widest text-sm px-3 py-2 border-b-2 transition-colors whitespace-nowrap ${
    isActive
      ? "text-amber-brass border-[var(--accent-amber)]"
      : "text-cream border-transparent hover:text-amber-brass"
  }`;

const garageDesktopClass = ({ isActive }) =>
  `ml-3 font-display uppercase tracking-widest text-sm px-4 py-2 border transition-all flex items-center gap-1.5 whitespace-nowrap ${
    isActive
      ? "bg-[var(--accent-amber)] text-shop border-[var(--accent-amber)]"
      : "bg-elevated text-amber-brass border-[var(--accent-amber)] hover:bg-[var(--accent-amber)] hover:text-shop"
  }`;

const mobileLink = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-3 border-l-2 font-display uppercase tracking-widest text-base ${
    isActive
      ? "border-[var(--accent-amber)] text-amber-brass bg-elevated"
      : "border-transparent text-cream hover:bg-elevated"
  }`;

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close mobile drawer on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    nav("/");
  };

  return (
    <div className="min-h-screen bg-garage">
      <header className="border-b border-shop bg-panel/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-5 py-3 gap-3">
          {/* Logo */}
          <Link to="/" data-testid="logo-home" className="flex items-center gap-2 group shrink-0">
            <div className="w-9 h-9 border border-shop-strong flex items-center justify-center bg-elevated">
              <Wrench size={18} className="text-amber-brass" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-xl sm:text-2xl font-extrabold tracking-tight text-cream">
                CHROME<span className="text-red-candy">VAULT</span>
              </div>
              <div className="hidden sm:block font-mono text-[10px] text-secondary tracking-widest -mt-0.5">
                CLASSIC-CAR-ID // v1.0
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          {user && (
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_ITEMS.map((it) => (
                <NavLink key={it.to} to={it.to} className={desktopLink} data-testid={it.testid}>
                  {it.label}
                </NavLink>
              ))}
              <NavLink to="/garage" data-testid="nav-garage" className={garageDesktopClass}>
                <Wrench size={14} /> Garage
              </NavLink>
            </nav>
          )}

          {/* Desktop right */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            {user ? (
              <button
                data-testid="logout-btn"
                className="ghost-btn text-xs flex items-center gap-1.5"
                onClick={handleLogout}
              >
                <LogOut size={14} /> Logout
              </button>
            ) : (
              <>
                <Link data-testid="nav-login" to="/login" className="ghost-btn text-xs">Login</Link>
                <Link data-testid="nav-register" to="/register" className="chrome-btn text-xs">Sign Up</Link>
              </>
            )}
          </div>

          {/* Mobile hamburger — signed-in only shows if user; signed-out shows sign-up shortcut inline */}
          <div className="flex lg:hidden items-center gap-2 shrink-0">
            {!user && (
              <Link data-testid="nav-register" to="/register" className="chrome-btn text-xs px-3 py-1.5">
                Sign Up
              </Link>
            )}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMenuOpen((s) => !s)}
              className="w-10 h-10 flex items-center justify-center border border-shop-strong bg-elevated text-amber-brass"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {menuOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden border-t border-shop bg-shop"
          >
            {user ? (
              <>
                <nav className="py-2">
                  {NAV_ITEMS.map((it) => {
                    const Icon = it.icon;
                    return (
                      <NavLink
                        key={it.to}
                        to={it.to}
                        onClick={() => setMenuOpen(false)}
                        className={mobileLink}
                        data-testid={`m-${it.testid}`}
                      >
                        <Icon size={16} /> {it.label}
                      </NavLink>
                    );
                  })}
                  <NavLink
                    to="/garage"
                    data-testid="m-nav-garage"
                    onClick={() => setMenuOpen(false)}
                    className={mobileLink}
                  >
                    <Warehouse size={16} /> Garage
                  </NavLink>
                </nav>
                <div className="border-t border-shop px-4 py-3 flex items-center justify-between text-xs">
                  <span className="font-mono text-secondary truncate mr-3">{user.email}</span>
                  <button
                    data-testid="m-logout-btn"
                    onClick={handleLogout}
                    className="ghost-btn text-xs flex items-center gap-1.5 shrink-0"
                  >
                    <LogOut size={14} /> Logout
                  </button>
                </div>
              </>
            ) : (
              <nav className="py-2">
                <Link
                  to="/login"
                  data-testid="m-nav-login"
                  onClick={() => setMenuOpen(false)}
                  className="block px-4 py-3 font-display uppercase tracking-widest text-cream border-l-2 border-transparent hover:bg-elevated"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  data-testid="m-nav-register"
                  onClick={() => setMenuOpen(false)}
                  className="block px-4 py-3 font-display uppercase tracking-widest text-amber-brass border-l-2 border-[var(--accent-amber)] bg-elevated"
                >
                  Sign Up
                </Link>
              </nav>
            )}
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-5 py-6 sm:py-8">{children}</main>

      <footer className="mt-16 border-t border-shop py-6 text-center">
        <p className="font-mono text-[10px] sm:text-[11px] tracking-widest text-secondary uppercase px-4">
          ChromeVault // Built for gearheads // {new Date().getFullYear()}
        </p>
      </footer>
    </div>
  );
};

export default Layout;
